import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

const STORAGE_KEY = 'rpm_demo_mode';

function readLatch(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

// Only the two exact words command. A trimmed `?demo` or a mangled `?demo=1`
// falls through to the latch instead of un-blurring a live screen-share.
function parseCommand(raw: string | null): boolean | null {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return null;
}

// `?demo=true` blurs patient names for screen-sharing. A presentation aid, NOT
// redaction — the names stay in the DOM, the responses and the `title` tooltips.
export default function useDemoMode(): boolean {
  const [searchParams] = useSearchParams();
  const cmd = parseCommand(searchParams.get('demo'));
  // Latched because in-app navigation drops unknown query params (pickBoardParams
  // on the board round trip, the bare `/engage` sidebar target), so reading the URL
  // alone un-blurs on the first click. Lazy initializer, not a bare readLatch() in
  // the body: the compiler would cache that impure read in a memo slot (see useNow).
  const [latched, setLatched] = useState(() => (cmd == null ? readLatch() : cmd));

  useEffect(() => {
    if (cmd == null) return;
    try {
      if (cmd) sessionStorage.setItem(STORAGE_KEY, '1');
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch { /* storage blocked — the param still drives this navigation */ }
    setLatched(cmd);
  }, [cmd]);

  return cmd ?? latched;
}
